import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import type { LinkComponentProps } from '@locus-taxy/encore-mui';
import { getTestId, toKebabCase } from '@locus-taxy/encore-utils';
import {
  appendTeamsToHref,
  extractTeamsParam,
} from '../../utils/teamsQueryParam';
import { TestIdAction } from '../../utils/testIdUtils';

/**
 * A React Router Link that preserves the `teams` query parameter.
 * Used as the `linkComponent` slot for encore-mui's DashboardLayout.
 */
const RouterLink = forwardRef<HTMLAnchorElement, LinkComponentProps>(
  ({ href, children, onClick, className }, ref) => {
    const [searchParams] = useSearchParams();
    const teamsParam = extractTeamsParam(searchParams);
    const to = appendTeamsToHref(href, teamsParam);

    const segments = href.split('/').filter(Boolean);
    const lastSegment =
      segments.length > 0 ? segments[segments.length - 1] : 'home';
    const slug = toKebabCase(lastSegment);

    const testId = getTestId({
      action: TestIdAction.NAVIGATE,
      object: 'nav-item',
      entity: slug,
      page: 'sidebar',
    });

    return (
      <Link
        ref={ref}
        to={to}
        onClick={onClick}
        className={className}
        data-testid={testId}
      >
        {children}
      </Link>
    );
  }
);

RouterLink.displayName = 'RouterLink';

export default RouterLink;
